'use client'

/**
 * Shared primitives for the Membership Card design language — the
 * /account redesign in step 0. Used across every account surface
 * (Profile, Subscription, Purchases, …) so each tab inherits the same
 * visual vocabulary: card with an accent stripe on top, eyebrow
 * micro-label, dotted-divider record rows, click-row quick actions.
 *
 * Square-edged across the board — no `rounded-*` classes. Status dots
 * and the avatar circle are the only inherent circular shapes.
 */

import { ChevronRight } from 'lucide-react'
import {
  useEffect,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from 'react'

// ─── Eyebrow ──────────────────────────────────────────────────────
//
// Tiny mono uppercase label that sits above section content. The design
// uses it on every SurfaceCard to identify what the card is for without
// taking a full heading-sized line.

export function Eyebrow({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 ${className}`}
    >
      {children}
    </div>
  )
}

// ─── SurfaceCard ──────────────────────────────────────────────────
//
// The standard "card containing a section of account data" primitive.
// Always a square white card with a 3px accent stripe across the top,
// optional eyebrow, optional title, then arbitrary children.

export function SurfaceCard({
  children,
  title,
  eyebrow,
  accent = 'var(--account-accent, var(--brand-700))',
  className = '',
}: {
  children: ReactNode
  title?: ReactNode
  eyebrow?: ReactNode
  /** CSS color for the 3px accent stripe — pass a token like
   *  `var(--rope)` or `var(--ink)` to match the section. Defaults
   *  to `--account-accent` (set on the /account page wrapper based
   *  on direct-vs-Patreon membership) with a blue fallback. */
  accent?: string
  className?: string
}) {
  return (
    <div className={`relative bg-card border border-border px-7 py-6 ${className}`}>
      {/* Accent stripe across the top */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: accent }}
        aria-hidden
      />
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      {title ? (
        <div className="mb-4 text-[22px] font-bold tracking-tight">{title}</div>
      ) : null}
      {children}
    </div>
  )
}

// ─── RecordRow ────────────────────────────────────────────────────
//
// A horizontal "field row" — label on the left, value on the right,
// optional badge + action button. Rows separate via a thin dotted
// border. `first` drops that border on the first row in a card so the
// card edge reads cleanly.

export function RecordRow({
  k,
  v,
  action,
  onAction,
  badge,
  first,
  danger,
}: {
  /** Label — the dim left column. */
  k: ReactNode
  /** Value — the bold right column. */
  v: ReactNode
  /** Optional trailing action button (e.g. "Edit", "Change"). */
  action?: ReactNode
  onAction?: () => void
  /** Optional inline status pill — e.g. VERIFIED / ENABLED / LINKED. */
  badge?: { label: string; bg?: string; fg?: string }
  /** Drop the top dotted border (use on the first row of a card). */
  first?: boolean
  /** Style the action in red — used for destructive actions. */
  danger?: boolean
}) {
  // On phones the row stacks: label on its own line, then value /
  // badge / action on the next. On `sm` (≥640px) and up, the original
  // side-by-side layout returns with a fixed 180px label column.
  return (
    <div
      className={`py-3.5 ${
        first ? '' : 'border-t border-dotted border-border'
      } flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3`}
    >
      <div className="text-xs text-muted-foreground sm:flex-[0_0_180px]">{k}</div>
      <div className="flex items-baseline justify-between gap-2 sm:flex-1">
        <div className="flex-1 text-sm font-medium break-words min-w-0">{v}</div>
        {badge ? (
          <span
            className="text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 whitespace-nowrap"
            style={{
              color: badge.fg || '#15803D',
              background: badge.bg || '#DCFCE7',
              marginRight: action ? 12 : 0,
            }}
          >
            {badge.label}
          </span>
        ) : null}
        {action ? (
          <button
            type="button"
            onClick={onAction}
            className={`bg-transparent border-0 text-xs font-semibold whitespace-nowrap ${
              danger ? 'text-red-600' : 'text-brand-700 dark:text-brand-400'
            }`}
          >
            {action}
          </button>
        ) : null}
      </div>
    </div>
  )
}

// ─── QuickAction ──────────────────────────────────────────────────
//
// Clickable row of icon + label + trailing chevron. Used on the
// Account Actions card and the Faster-than-email card.

export function QuickAction({
  icon: Icon,
  label,
  danger,
  onClick,
  href,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>
  label: ReactNode
  danger?: boolean
  onClick?: () => void
  href?: string
}) {
  const Tag = href ? 'a' : 'button'
  return (
    <Tag
      // a-tag needs href, button-tag needs type
      {...(href ? { href } : { type: 'button' as const })}
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full text-left py-2.5 text-sm border-t border-border bg-transparent cursor-pointer ${
        danger ? 'text-red-600' : 'text-foreground'
      }`}
    >
      <Icon width={15} height={15} aria-hidden />
      <span className="flex-1">{label}</span>
      <ChevronRight width={14} height={14} aria-hidden />
    </Tag>
  )
}

// ─── Toggle ──────────────────────────────────────────────────────
//
// Pill switch used on Preferences and the Notifications channels
// matrix. Toggles are the one UX convention where "rounded" reads as
// universal — every modern OS / web framework renders them as pills —
// so this primitive uses `rounded-full` even though the rest of the
// design language is square. Slightly larger track + circular dot
// with a soft shadow give it a tactile feel.

export function Toggle({
  on,
  locked,
  onChange,
  ariaLabel,
}: {
  on: boolean
  locked?: boolean
  onChange?: (next: boolean) => void
  ariaLabel?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={locked}
      onClick={() => onChange?.(!on)}
      className={`relative inline-flex items-center w-[46px] h-[26px] rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 ${
        locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      }`}
      style={{
        background: on ? 'var(--brand-500, #0008EF)' : 'rgba(95,100,122,0.35)',
      }}
    >
      <span
        aria-hidden
        className="absolute top-[3px] w-[20px] h-[20px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.18),0_1px_2px_rgba(0,0,0,0.1)] transition-[left] duration-200"
        style={{ left: on ? 23 : 3 }}
      />
    </button>
  )
}

// ─── ModalOverlay ────────────────────────────────────────────────
//
// Generic centered modal — fixed full-viewport backdrop, click-out
// to close, Esc to close, body-scroll lock while open. Used for the
// "Are you sure you want to delete your account" + Change Password
// confirmations so they can't be missed in the page flow.

export function ModalOverlay({
  open,
  onClose,
  children,
  width = 'max-w-lg',
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Tailwind max-width class for the dialog. Default `max-w-lg`. */
  width?: string
}) {
  // Esc-to-close + body-scroll lock while the modal is open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-card border border-border shadow-2xl mt-12 max-h-[calc(100vh-6rem)] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}
